<template>
    <div>
<!-- DIALOGS -->
        <SelectionRequiredDialog />
        <PeriodRequiredDialog />
        <PublishingStatementsRoyaltyControllingDialog />
        <DeletionDialog context='SelectedPublishingStatementsArchive' />	
        <PublishingStatementsArchivingDialog />
        <StatementPreviewDialog context="PublishingStatements" dir='publishing/statements' />
        <ContextHelpDialog />
        <ConfirmationDialog />
        <AzPersonsAssignTemplatesDialog />
        <BaseListPage
            headline="lang_PUBLISHING_STATEMENTS"
            :context="context"
            :alvqa="alvqa"
            :stickyHeader="true"
        >
        <!-- FILTER -->
            <template v-slot:filter>
                <v-col cols="2">
                    <DropdownFilter
                        :context="context + 'ArchiveFilter'"
                        label="lang_GENERIC_CAPTION_FILTER"
                        :items="archiveFilterItems"
                        :defaultValueNull="true"
                        :alvqa="alvqa + '-list-dropdown-archive-filter'"
                    />
                </v-col>
            </template>
        <!-- PERIOD -->
            <template v-slot:period>
                <v-col cols="6" class="pa-0">
                    <BasePeriod 
                        class="pt-3 pl-3"
                        :context="context" 
                        widthYearField="2"
                        widthNumberField="1"
                        periodLabel="lang_GENERIC_CAPTION_PERIOD"
                    />
                </v-col>
            </template>
        <!-- DATA TABLE -->
            <template v-slot:dataTable>                
                <BaseDataTableList 
                    :headers="headers"
                    :context="context"      
                    :showExpand="true"
                    readOnlyGhostTdSpan="2"
                    :alvqa="alvqa"
                    :differentItemsPerPage="900"
                    :hasServerSideSorting="true"
                    :stickyTopSlot="true"
                >
                <!-- TOPSLOT: actions, imports etc. -->
                    <template v-slot:topSlot><PublishingStatementsTopSlot /></template>
                <!-- DATA TABLE CONTENT: columns -->
                    <template v-slot:body="{ item }">
                        <ExpandIcon 
                            :isExpanded="item.isExpanded"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-icon-expand'"
                            :color="item.item.archiveRowsExist ? 'standardBrownIcon' : 'buttonStandard'"
                        />
                            <!-- :color="item.item.archiveRowsExist ? 'buttonStandard' : 'standardBrownIcon'" -->
                        <td 
                            class="ellipsis"
                            :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-licensor'"
                        >
                        <BaseTooltip>
                            <template v-slot:text>
                                {{ item.item.licensor !== null ? $alvPlugin.showNameOfAzEntry(item.item.licensor) + ' (' + $t('lang_GENERIC_CAPTION_ALVID') +': ' + item.item.licensor.alvId + ')': '' }}
                            </template>
                            <template v-slot:content>
                                <span :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-licensor-name'">
                                    {{ item.item.licensor !== null ? $alvPlugin.showNameOfAzEntry(item.item.licensor) : '' }}
                                </span>
                            </template>
                        </BaseTooltip>
                        </td>
                        <td class="no_ellipsis">
                            <div :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-last-created-on'">
                                {{ item.item.lastCreatedOn !== null ? $d(new Date(item.item.lastCreatedOn), 'alvDateTime',  $i18n.locale) : '' }}
                            </div>
                        </td>
                        <td class="no_ellipsis text-right">
                            <div :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-period'">
                                {{ $alvPlugin.displayPeriod(item.item.period) }}
                            </div>
                        </td>
                        <td class="no_ellipsis text-right">
                            <div :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-amount'">
                                {{ $alvPlugin.formatMoney(item.item.amount) }}
                            </div>
                        </td>
                        <td class="no_ellipsis text-right">
                            <div :alvqa="alvqa + '-list-row-' + item.item.alvId + '-text-net-before-advances'">
                                {{ item.item.netBeforeAdvances !== null && item.item.netBeforeAdvances !== undefined ? $alvPlugin.formatMoney(item.item.netBeforeAdvances) : '' }}
                            </div>
                        </td>
                        <td class="no_ellipsis">
                            <v-chip
                                v-if="item.item.payee"
                                small
                                color="buttonNewEntity"
                                dark
                                :alvqa="alvqa + '-list-row-' + item.item.alvId + '-chip-payee'"
                            >
                                {{ $t('lang_PUBLISHING_STATEMENTS_CAPTION_PAYEE') }}
                            </v-chip>
                        </td>
                    </template>
                    <template v-slot:expandSlot="{ id }"><PublishingStatementsReadOnly :itemId="id"/></template>
                </BaseDataTableList>
            </template>
        </BaseListPage>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'

import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseListPage from '@/components/_base/BaseListPage'
import BasePeriod from '@/components/_base/BasePeriod'
import DropdownFilter from '@/components/_filters/DropdownFilter'
import ExpandIcon from '@/components/_buttons/ExpandIcon'

import PublishingStatementsTopSlot from './PublishingStatementsTopSlot'
import PublishingStatementsReadOnly from './_readonly/PublishingStatementsReadOnly'
import PublishingStatementsArchivingDialog from './_dialogs/PublishingStatementsArchivingDialog'
import DeletionDialog from '@/components/_dialogs/DeletionDialog'
import StatementPreviewDialog from '@/components/_dialogs/StatementPreviewDialog'

import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'
import PeriodRequiredDialog from '@/components/_dialogs/PeriodRequiredDialog'
import PublishingStatementsRoyaltyControllingDialog from './_dialogs/PublishingStatementsRoyaltyControllingDialog'
import ContextHelpDialog from '@/components/_dialogs/ContextHelpDialog'
import ConfirmationDialog from '@/components/_dialogs/ConfirmationDialog'
import AzPersonsAssignTemplatesDialog from '@/pages/5_az/2_persons/_dialogs/AzPersonsAssignTemplatesDialog'

export default {
    name: "PublishingStatementsList",  
    components: {
        BaseListPage,
        BaseDataTableList,
        BaseTooltip,
        BasePeriod,
        DropdownFilter,
        ExpandIcon,

        PublishingStatementsTopSlot,
        PublishingStatementsReadOnly,
        PublishingStatementsArchivingDialog,
        DeletionDialog,
        StatementPreviewDialog,
        
        SelectionRequiredDialog,
        PeriodRequiredDialog,
        PublishingStatementsRoyaltyControllingDialog,
        ContextHelpDialog,
        ConfirmationDialog,
        AzPersonsAssignTemplatesDialog,
    },
    data() {
        return {
            context: "PublishingStatements",
            alvqa: "publishing-statements"
        }
    },
    computed: {
        ...mapGetters([
            "getPublishingStatementsSearch", // Keep your existing getter
            "getSelectedPublishingStatements", // New: Tracks selected checkboxes
            "getPublishingStatementsArchivingTargetCurrency", // New: Tracks selected target currency
            "getSelectedPeriodYearPublishingStatements"
        ]),

        isPeriodSet() {
            const y = this.getSelectedPeriodYearPublishingStatements
            return y !== null && y !== undefined && y !== ''
        },

        archiveFilterItems() {
            const periodRequired = !this.isPeriodSet
            const suffix = periodRequired ? this.$t('lang_GENERIC_CAPTION_PERIOD_REQUIRED_SUFFIX') : ''
            return [
                {
                    text: this.$t('lang_GENERIC_CAPTION_WITH_CARRY_FORWARD') + suffix,
                    value: 'archivedWithCarryForward',
                    disabled: periodRequired,
                    alvqa: this.alvqa + '-list-dropdown-archive-filter-with-carry-forward'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_WITHOUT_CARRY_FORWARD') + suffix,
                    value: 'archivedWithoutCarryForward',
                    disabled: periodRequired,
                    alvqa: this.alvqa + '-list-dropdown-archive-filter-without-carry-forward'
                }
            ]
        },

		// Returns an Array with header-objects with props.
        headers() {
            return [
				{
					text: '', 
					value: 'data-table-select', 
					align: 'center',
                    width: "60px",
                    sortable: false,
                    class: 'text-no-wrap'
				},
                {
					text: '', 
					value: 'data-table-expand',
					align: 'center', 
                    sortable: false,
					width: "48px",
				},
                {
                    text: this.$t('lang_GENERIC_CAPTION_LICENSOR'), 
                    value: 'licensor',
                    align: 'left',
                    sortable: true,
                    class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_PUBLISHING_STATEMENTS_CAPTION_LAST_CREATED_ON'), 
                    value: 'lastCreatedOn',
                    align: 'left', 
                    sortable: true,
                    width: "170px",
                    class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_PERIOD'), 
                    value: 'period',
                    align: 'right', 
                    sortable: false,
                    width: "130px",
                    class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_AMOUNT') + ' ' + this.$store.getters.getSelectedCurrency, 
                    value: 'amount',
                    align: 'end', 
                    sortable: true,
                    width: "140px",
                    class: 'text-no-wrap'
                },
                {
                    text: this.$t('lang_GENERIC_CAPTION_RESULT') + ' ' + this.$store.getters.getSelectedCurrency,
                    value: 'netBeforeAdvances',
                    align: 'end',
                    sortable: true,
                    width: "160px",
                    class: 'text-no-wrap'
                },
                {
                    text: '',
                    value: 'payee',
                    align: 'left',
                    sortable: false,
                    width: "100px",
                    class: 'text-no-wrap'
                },
                {
                    text: '', 
                    value: 'downloadLink',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                }
            ]
        }
    },
    watch: {
        getSelectedPublishingStatements: {
            handler() {
                // Get selected statement IDs from Vuex
                const selectedStatements = this.$store.getters.getSelectedPublishingStatements;
                // console.log("🔹 Selected Statements:", selectedStatements);

                if (!selectedStatements || selectedStatements.length === 0) {
                    // console.log("⚠️ No statements selected, hiding CcfField");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", false);
                    return;
                }

                // Get home currency from Vuex
                const homeCurrency = this.$store.getters.getSelectedCurrency;
                if (!homeCurrency) {
                    // console.warn("⚠️ Home Currency is missing! Defaulting to hiding CcfField.");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", false);
                    return;
                }
                // console.log("🏠 Home Currency:", homeCurrency);

                // Get all publishing statements from Vuex
                const allStatements = this.$store.getters.getPublishingStatements;
                if (!allStatements || allStatements.length === 0) {
                    // console.warn("⚠️ No publishing statements found in store.");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", false);
                    return;
                }

                // Match selected statements to full objects
                const selectedStatementsObjects = allStatements.filter(statement => 
                    selectedStatements.includes(statement.alvId)
                );

                // console.log("📌 Selected Statement Objects:", selectedStatementsObjects);

                if (selectedStatementsObjects.length === 0) {
                    // console.warn("⚠️ No matching statements found in getPublishingStatements.");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", false);
                    return;
                }

                // Extract and filter valid currencies **(explicit checks, no optional chaining)**
                const selectedCurrencies = selectedStatementsObjects
                    .map(statement => {
                        if (
                            statement &&
                            statement.licensor &&
                            statement.licensor.metadata &&
                            statement.licensor.metadata.currency
                        ) {
                            return statement.licensor.metadata.currency;
                        }
                        return null;
                    })
                    .filter(currency => currency !== null); // Remove nulls

                // console.log("💱 Selected Currencies (excluding nulls):", selectedCurrencies);

                if (selectedCurrencies.length === 0) {
                    // console.warn("⚠️ No valid currencies found in selected statements.");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", false);
                    return;
                }

                // Ensure all selected statements share the **same** currency (and it's NOT home currency)
                const uniqueCurrencies = new Set(selectedCurrencies);

                if (uniqueCurrencies.size === 1 && !uniqueCurrencies.has(homeCurrency)) {
                    // console.log("✅ Single non-home currency found, showing CcfField");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", true);
                    this.$store.commit("setPublishingStatementsArchivingTargetCurrency", Array.from(uniqueCurrencies)[0]);
                } else {
                    // console.log("❌ Multiple currencies found or same as homeCurrency, hiding CcfField");
                    this.$store.commit("setPublishingStatementsArchivingShowCcfField", false);
                }
            },
            deep: true,
            immediate: true
        }
    }



}
</script>